import { StringID } from "userful-chronos-app-common-js/dist/models/common";

import "./ClustersCard.scss";
import { IClusterInfo, IConstellationInfo, ClusterRemoteManagement } from "../../../model/CarbonExternalModels";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faCircleNodes,
    faCodeBranch,
    faCodeFork,
    faCube,
    faDisplay,
    faExternalLink,
    faFileCertificate,
    faGlobe,
    faHardDrive,
    faMultiply,
    faRefresh,
    faServer,
} from "@fortawesome/pro-regular-svg-icons";
import { UserfulTooltip } from "userful-chronos-common-ui/dist";
import ClusterCardDropdown from "./dropdown/ClusterCardDropdown";
import { t } from "i18next";
import { getNumberOfNodes, getTotalNumberOfUClients } from "../utils/clusterNodes";
import { useState } from "react";
import { runTaskAndWaitForResponse } from "userful-chronos-app-common-js/dist/message/msgQueue";
import { sendRemoteConnectionRequest } from "../../../messages/messageSender";
import { RESPOND_REMOTE_CONNECTION } from "../../../messages/messageTypes";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";

interface IProps {
    foundObject: { cluster: IClusterInfo; constellation: IConstellationInfo };
    crm: { value: ClusterRemoteManagement };
    onRowClick?(id: StringID): void;
}

function getAccessUrl(defaultUrl: string, crm: ClusterRemoteManagement) {
    if (crm && crm.address && crm.address !== "") {
        if (crm.port > 0) {
            return crm.address + ":" + crm.port;
        }
        return crm.address;
    }
    return defaultUrl;
}

export default function ClustersCard({ foundObject, crm, onRowClick }: IProps) {
    const totalNumberOfDevices = getNumberOfNodes(foundObject.cluster);
    const totalUClients = getTotalNumberOfUClients(foundObject, totalNumberOfDevices);
    const [loading, setLoading] = useState(false);

    let display = undefined;

    if (foundObject.cluster.license.failOver) {
        const foundCluster = foundObject.cluster.license.clusterGroup.find(
            (clusterGroup) => clusterGroup.id.value === foundObject.cluster.id.value,
        );

        if (foundCluster?.primary) {
            display = true;
        } else {
            display = false;
        }
    } else {
        display = true;
    }

    if (!display) return null;

    const openRemoteManagement = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setLoading(true);
        runTaskAndWaitForResponse(
            () => sendRemoteConnectionRequest({ id: foundObject.cluster.id }),
            RESPOND_REMOTE_CONNECTION,
            (data) => {
                if (data?.id.value === foundObject.cluster.id.value) {
                    window.open(`${data.uiUri}?gcm=t&clusterid=${foundObject.cluster.id.value}`, "_blank").focus();
                    return true;
                }
                return false;
            },
            `Open remote management for ${foundObject.cluster.id.value}`,
        ).then(() => setLoading(false));
    };

    return (
        <div className="clustersCard noselect" onClick={(e) => onRowClick(foundObject.cluster.id)}>
            <div className="clusterCardHeader">
                <div className="clusterCardSubHeader">
                    <FontAwesomeIcon icon={faCircleNodes} />
                    <p>{foundObject.cluster.name}</p>
                </div>
                <div className="clusterCardSubHeader">
                    {foundObject.cluster.status === "CONNECTED" && (
                        <>
                            <UserfulTooltip title="Go to Remote Management">
                                <a
                                    onClick={(e) => openRemoteManagement(e)}
                                    target="_blank"
                                    rel="noreferrer"
                                    // href={getAccessUrl(foundObject.cluster.accessConfig.accessUrl, crm.value)}
                                    className="buttonLink"
                                >
                                    <FontAwesomeIcon icon={faExternalLink} />
                                </a>
                            </UserfulTooltip>
                        </>
                    )}
                    <ClusterCardDropdown foundObject={foundObject} />
                </div>
            </div>
            <div className="clusterCardBody">
                <div className="clusterCardBodyRow">
                    <div className="clusterCardBodyColumn">
                        <FontAwesomeIcon className="card-icon" icon={faGlobe} />
                        <p>IP Address</p>
                    </div>
                    <div className="clusterCardBodyColumn">
                        <p>{foundObject.cluster.ip}</p>
                    </div>
                </div>
                <div className="clusterCardBodyRow">
                    <div className="clusterCardBodyColumn">
                        <FontAwesomeIcon className="card-icon" icon={faCodeBranch} />
                        <p>Version</p>
                    </div>
                    <div className="clusterCardBodyColumn">
                        <p>Userful {foundObject.cluster.version.length >= 1 ? foundObject.cluster.version : "X.X"}</p>
                    </div>
                </div>
                <div className="clusterCardBodyRow">
                    <div className="clusterCardBodyColumn">
                        <FontAwesomeIcon className="card-icon" icon={faGlobe} />
                        <p>Status</p>
                    </div>
                    <div className="clusterCardBodyColumn">
                        {foundObject.cluster.status === "CONNECTED" && (
                            <div className="cluster normalGap default-text">
                                <FontAwesomeIcon className="green" icon={faCheck} />
                                <p>Online</p>
                            </div>
                        )}
                        {foundObject.cluster.status === "DISCONNECTED" && (
                            <div className="cluster normalGap default-text">
                                <FontAwesomeIcon className="red" icon={faMultiply} />
                                <p>Offline</p>
                            </div>
                        )}
                    </div>
                </div>
                <div className="clusterCardBodyRow">
                    <div className="clusterCardBodyColumn">
                        <FontAwesomeIcon className="card-icon" icon={faFileCertificate} />
                        <p>License Status</p>
                    </div>
                    <div className="clusterCardBodyColumn">
                        {foundObject.cluster.license.active && (
                            <div className="cluster normalGap default-text">
                                <FontAwesomeIcon className="green" icon={faCheck} />
                                <p>Active</p>
                            </div>
                        )}
                        {!foundObject.cluster.license.active && (
                            <div className="cluster normalGap default-text">
                                <FontAwesomeIcon className="red" icon={faMultiply} />
                                <p>Inactive</p>
                            </div>
                        )}
                    </div>
                </div>
                <div className="clusterCardBodyRow">
                    <div className="clusterCardBodyColumn">
                        <FontAwesomeIcon className="card-icon" icon={faRefresh} />
                        <p>Failover Status</p>
                    </div>
                    <div className="clusterCardBodyColumn">
                        {foundObject.cluster.license.failOver && (
                            <div className="cluster normalGap default-text">
                                <FontAwesomeIcon className="green" icon={faCheck} />
                                <p>Setup</p>
                            </div>
                        )}
                        {!foundObject.cluster.license.failOver && (
                            <div className="cluster normalGap default-text">
                                <FontAwesomeIcon className="red" icon={faMultiply} />
                                <p>Not Setup</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="clusterCardBody">
                <div className="clusterCardBodyRow">
                    <div className="clusterCardBodyColumn">
                        <FontAwesomeIcon className="card-icon" icon={faCodeFork} />
                        <p>Mapping Groups</p>
                    </div>
                    <div className="clusterCardBodyColumn">
                        <p>{foundObject.cluster.mappingGroups}</p>
                    </div>
                </div>
                <div className="clusterCardBodyRow">
                    <div className="clusterCardBodyColumn">
                        <FontAwesomeIcon className="card-icon" icon={faServer} />
                        <p>Nodes</p>
                    </div>
                    <div className="clusterCardBodyColumn">
                        <p>{totalNumberOfDevices}</p>
                    </div>
                </div>
                <div className="clusterCardBodyRow">
                    <div className="clusterCardBodyColumn">
                        <FontAwesomeIcon className="card-icon" icon={faHardDrive} />
                        <p>End-Points</p>
                    </div>
                    <div className="clusterCardBodyColumn">
                        <p>{totalUClients}</p>
                    </div>
                </div>
                <div className="clusterCardBodyRow">
                    <div className="clusterCardBodyColumn">
                        <FontAwesomeIcon className="card-icon" icon={faCube} />
                        <p>Active Sources</p>
                    </div>
                    <div className="clusterCardBodyColumn">
                        <p>{foundObject.cluster.activeSources}</p>
                    </div>
                </div>
                <div className="clusterCardBodyRow">
                    <div className="clusterCardBodyColumn">
                        <FontAwesomeIcon className="card-icon" icon={faDisplay} />
                        <p>Active Destinations</p>
                    </div>
                    <div className="clusterCardBodyColumn">
                        <p>{foundObject.cluster.activeDestinations}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
